import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Project } from '../../types/domain';
import { apiClient } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { ConfirmModal } from '../modals/ConfirmModal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Save, Loader2, Trash2, AlertTriangle } from 'lucide-react';

interface SettingsTabProps {
  table: Table;
  project: Project;
  canEdit: boolean;
  onTableUpdated: (table: Table) => void;
  activeSubTab?: 'general' | 'danger';
  onSubTabChange?: (tab: 'general' | 'danger') => void;
  // Optional for backward compatibility
  columns?: any[];
  onColumnsUpdated?: (columns: any[]) => void;
}

export const SettingsTab: React.FC<SettingsTabProps> = ({
  table,
  project,
  canEdit,
  onTableUpdated,
  activeSubTab: controlledSubTab,
  onSubTabChange,
}) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { refreshTables } = useAuth();

  const [name, setName] = useState(table.name);
  const [description, setDescription] = useState(table.description || '');
  const [isActive, setIsActive] = useState(table.is_active !== false);
  const [isSaving, setIsSaving] = useState(false);

  // Uncontrolled fallback if not passed from TableView
  const [uncontrolledSubTab, setUncontrolledSubTab] = useState<'general' | 'danger'>('general');
  const activeTab = controlledSubTab !== undefined ? controlledSubTab : uncontrolledSubTab;

  // Datastore Deletion State
  const [deleteDatastoreModalOpen, setDeleteDatastoreModalOpen] = useState(false);
  const [isDeletingDatastore, setIsDeletingDatastore] = useState(false);

  useEffect(() => {
    setName(table.name);
    setDescription(table.description || '');
    setIsActive(table.is_active !== false);
  }, [table.id, table.name, table.description, table.is_active]);

  const executeDeleteDatastore = async () => {
    if (!canEdit) return;
    setIsDeletingDatastore(true);
    try {
      await apiClient(`/projects/${project.id}/tables/${table.id}`, {
        method: 'DELETE',
      });
      showToast({
        type: 'success',
        title: 'Datastore Deleted',
        message: `Datastore '${table.name}' was permanently deleted.`,
      });
      setDeleteDatastoreModalOpen(false);
      await refreshTables();
      navigate('/dashboard');
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Failed to delete datastore',
        message: err?.message || 'Server error',
      });
    } finally {
      setIsDeletingDatastore(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEdit) return;

    setIsSaving(true);
    try {
      const res = await apiClient<any>(
        `/projects/${project.id}/tables/${table.id}`,
        {
          method: 'PUT',
          body: JSON.stringify({
            name: name.trim(),
            description: description.trim(),
            is_active: isActive,
          }),
        }
      );

      const updated = res.table || res || {
        ...table,
        name: name.trim(),
        description: description.trim(),
        is_active: isActive,
      };

      showToast({
        type: 'success',
        title: 'Settings Saved',
        message: `Datastore configuration for '${updated.name}' updated successfully.`,
      });

      onTableUpdated(updated);
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Failed to save settings',
        message: err?.message || 'Server error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-8 pb-6 min-w-0 max-w-full">
      {/* Tab 1: General Settings */}
      {activeTab === 'general' && (
        <form onSubmit={handleSubmit} className="space-y-8 text-xs">
          {/* General Info */}
          <div className="p-5 rounded-lg bg-cf-surface border border-cf-border space-y-4">
            <h3 className="font-semibold text-cf-textPrimary text-xs uppercase tracking-wide">
              General Identification
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Datastore Name"
                type="text"
                required
                disabled={!canEdit}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />

              <Input
                label="Description"
                type="text"
                disabled={!canEdit}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of this table's purpose"
              />
            </div>
          </div>

          {/* Ingestion & Live Status */}
          <div className="p-5 rounded-lg bg-cf-surface border border-cf-border space-y-4">
            <div>
              <h3 className="font-semibold text-cf-textPrimary text-xs uppercase tracking-wide">
                Ingestion &amp; Live Status
              </h3>
              <p className="text-xs text-cf-textSecondary mt-0.5">
                Control whether this datastore is actively accepting live incoming form submissions and webhook payloads.
              </p>
            </div>

            <div className="flex items-center gap-3 pt-1">
              <Button
                type="button"
                disabled={!canEdit}
                variant={isActive ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setIsActive(true)}
                leftIcon={<span className="w-1.5 h-1.5 rounded-full bg-emerald-600 dark:bg-emerald-400" />}
                className={isActive ? 'bg-emerald-500/10 dark:bg-emerald-500/20 border-emerald-500/30 text-emerald-700 dark:text-emerald-300 font-semibold' : ''}
              >
                Live (Accept Submissions)
              </Button>

              <Button
                type="button"
                disabled={!canEdit}
                variant={!isActive ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setIsActive(false)}
                leftIcon={<span className="w-1.5 h-1.5 rounded-full bg-zinc-600 dark:bg-zinc-400" />}
                className={!isActive ? 'bg-zinc-200 dark:bg-zinc-700/50 border-zinc-400 dark:border-zinc-600 text-zinc-900 dark:text-zinc-200 font-semibold' : ''}
              >
                Paused (Reject Incoming)
              </Button>
            </div>
          </div>

          {/* Submit */}
          {canEdit && (
            <div className="flex justify-end pt-2">
              <Button
                type="submit"
                variant="primary"
                size="md"
                isLoading={isSaving}
                disabled={isSaving}
                leftIcon={<Save size={14} />}
              >
                Save Datastore Settings
              </Button>
            </div>
          )}
        </form>
      )}

      {/* Tab 2: Danger Zone */}
      {canEdit && activeTab === 'danger' && (
        <div className="p-5 rounded-lg bg-rose-500/5 border border-rose-500/30 space-y-4">
          <h2 className="text-sm font-semibold text-rose-400 flex items-center gap-2">
            <Trash2 size={16} />
            <span>Delete Datastore</span>
          </h2>

          <p className="text-xs text-cf-textSecondary leading-relaxed">
            Permanently remove datastore <strong className="text-cf-textPrimary">{table.name}</strong> and all of its recorded submission data, discovered schema columns, and ingress routes. This action cannot be undone.
          </p>

          <div className="pt-2">
            <Button
              type="button"
              variant="danger"
              size="md"
              onClick={() => setDeleteDatastoreModalOpen(true)}
              leftIcon={<Trash2 size={14} />}
            >
              Delete Datastore
            </Button>
          </div>
        </div>
      )}

      {/* Delete Datastore Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteDatastoreModalOpen}
        onClose={() => setDeleteDatastoreModalOpen(false)}
        onConfirm={executeDeleteDatastore}
        title="Delete Datastore Permanently"
        message={
          <>
            Are you sure you want to delete <strong className="text-cf-textPrimary font-semibold">{table.name}</strong>? All historical submissions, dynamic schemas, and notifications attached to this table will be deleted permanently.
          </>
        }
        confirmLabel="Delete Datastore"
        isLoading={isDeletingDatastore}
      />
    </div>
  );
};
